import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUser } from '../../../../store/slices/authSlice';
import { useLoading } from '../../../../components/LoadingProvider';
import api from '../../../../api/axios';
import { showSuccess, showError } from '../../../../utils/toast';
import { getIndStates, calculateAge, getCountryCodes } from '../../../../utils/common';
import useAuth from '../../../../hooks/useAuth';
import { getPublicImgUrl } from '../../../../utils/common';

export default function DoctorProfileEdit() {
    const fetchedRef = useRef(false);
    const { user, isAuthenticated, userRole } = useAuth({ redirectPath: '/login', allowedRoles: ['doctor'] });
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const indStates = getIndStates();
    const countryCodes = getCountryCodes();
    const { setIsLoading } = useLoading();

    const [previewUrl, setPreviewUrl] = useState(getPublicImgUrl() + '/simg.jpg');
    const fileInputRef = useRef(null);
    const [sigPreviewUrl, setSigPreviewUrl] = useState(getPublicImgUrl() + '/simg.jpg');
    const sigFileInputRef = useRef(null);
    const errorDivRef = useRef(null);
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [prefixOfName, setPrefixOfName] = useState([ 'Dr.', 'Prof.', 'Mr.', 'Mrs.', 'Ms.' ]);
    const [adminApproved, setAdminApproved] = useState(true);
    const [errors, setErrors] = useState({
        profile_pic: '',
        signature_pic: '',
        name: '',
        dob: '',
        gender: '',
        address: {
            line1: '',
            district: '',
            state: '',
            country: '',
            pincode: ''
        },
        email: '',
        specializations: '',
        registrationNumber: '',
        medicalCouncil: '',
        yearsOfExperience: '',
        educationBackground: '',
        available_slots: '',
        nationalId: '',
        languagesSpoken: '',
        docCategories: '',
    });

    const [specializations, setSpecializations] = useState([]);
    const [categories, setCategories] = useState([]);
    const [otherSpecialization, setOtherSpecialization] = useState(false);
    const [formData, setFormData] = useState({
        namePrefix: '',
        name: user?.name || '',
        dob: '',
        gender: '',
        address: {
            line1: '',
            line2: '',
            district: '',
            state: '',
            country: 'India',
            pincode: ''
        },
        email: '',
        phoneCode: '',
        phoneNumber: '',
        contactNumber: user?.phone || '',
        bio: '',
        otherSpecialization: '',
        specializations: [],
        registrationNumber: '',
        medicalCouncil: '',
        yearsOfExperience: '',
        educationBackground: '',
        previousReports: [],
        availability: {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
        },
        available_slots: {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: [],
        },
        consultationFee: '',
        panCardNumber: '',
        upiId: '',
        accountNumber: '',
        ifscCode: '',
        accountHolderName: '',
        bankName: '',
        nationalId: '',
        website: '',
        languagesSpoken: '',
        symptoms: '',
        docCategories: [],
    });

    useEffect(() => {
        document.title = 'Varosa - Doctor Profile Edit';
        // Check authentication status
        if (!isAuthenticated || !user) {
            navigate('/login');
            return;
        }
    }, [isAuthenticated, user, navigate]);

    const fetchSpecializationList = useCallback(async () => {
        setBtnDisabled(true);
        try {
            const response = await api.get('/'+userRole+'/show-specializations');
            if (response?.data?.result === 'success') {
                setSpecializations(response.data.data.specializations);
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
        } finally {
            setBtnDisabled(false);
        }
    }, []);

    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        fetchSpecializationList();
        fetchDoctorData();
    }, []);

    const validateForm = () => {
        let all_good = true;
        let error = { 
            name: '',
            namePrefix: '',
            dob: '',
            gender: '',
            address: {
                line1: '',
                district: '',
                state: '',
                country: '',
                pincode: ''
            },
            email: '',
            specializations: '',
            registrationNumber: '',
            medicalCouncil: '',
            yearsOfExperience: '',
            educationBackground: '',
            available_slots: '',
        };

        if(formData.namePrefix.trim() === ''){
            error.namePrefix = 'Please select a valid prefix.';
            all_good = false;
        }
        if(formData.name.trim() === '' || formData.name.trim().length < 3){
            error.name = 'Please enter a valid name.';
            all_good = false;
        }
        if(formData.dob.trim() === '' || calculateAge(formData.dob.trim()) < 18){
            error.dob = 'Please enter a valid DOB (at least 18 years old).';
            all_good = false;
        }
        if(formData.gender.trim() === ''){
            error.dob = 'Please select a valid gender.';
            all_good = false;
        }
        if(formData.address.line1.trim() === ''){
            error.address.line1 = 'Please enter a valid address.';
            all_good = false;
        }
        if(formData.address.district.trim() === ''){
            error.address.district = 'Please enter a valid district.';
            all_good = false;
        }
        if(formData.address.state.trim() === ''){
            error.address.state = 'Please select a valid state.';
            all_good = false;
        }
        if(formData.address.country.trim() === ''){
            error.address.country = 'Please enter a valid country.';
            all_good = false;
        }
        if(formData.address.pincode.trim().replace(/\D/g, '').length != 6){
            error.address.pincode = 'Please enter a valid pincode (6-digits).';
            all_good = false;
        }
        if(!/^\S+@\S+\.\S+$/.test(formData.email.trim())){
            error.email = 'Please enter a valid email.';
            all_good = false;
        }
        if(formData.specializations.length === 0){
            error.specializations = 'Please select at least one specialization.';
            all_good = false;
        }
        if(formData.registrationNumber.trim() === ''){
            error.registrationNumber = 'Please enter a valid registration number.';
            all_good = false;
        }
        if(formData.medicalCouncil.trim() === ''){
            error.medicalCouncil = 'Please enter a valid medical council.';
            all_good = false;
        }
        if(formData.educationBackground.trim() === ''){
            error.educationBackground = 'Please enter a valid education background.';
            all_good = false;
        }
        if(formData.nationalId.trim() === ''){
            error.nationalId = 'Please enter a valid national id.';
            all_good = false;
        }
        if(formData.languagesSpoken.trim() === ''){
            error.languagesSpoken = 'Please enter a valid languages spoken.';
            all_good = false;
        }
        // for(let day in formData.available_slots){
        //     if(formData.availability[day] && formData.available_slots[day].length == 0){
        //         error.available_slots = 'Please select a valid available slots.';
        //         all_good = false;
        //         break;
        //     } else if(formData.availability[day] && formData.available_slots[day].length > 0){
        //         for(let slot of formData.available_slots[day]){
        //             if(slot.start.trim() == '' || slot.end.trim() == ''){
        //                 error.available_slots = 'Please select a valid available slots.';
        //                 all_good = false;
        //                 break;
        //             }else{
        //                 if(slot.start.trim() >= slot.end.trim()){
        //                     error.available_slots = 'Please select a valid available slots.';
        //                     all_good = false;
        //                     break;
        //                 }
        //             }
        //         }
        //     }
        // }
        setErrors(error);
        return all_good;
    };

    const fetchDoctorData = useCallback(async () => {
        setBtnDisabled(true);
        try {
            const response = await api.get('/'+userRole+'/details');
            if (response?.data?.result === 'success') {
                setFormData({
                    namePrefix: response.data.data.name_prefix || '',
                    name: response.data.data.name || '',
                    dob: response.data.data.dob || '',
                    gender: response.data.data.gender || '',
                    address: {
                        line1: response.data.data.address_line1 || '',
                        line2: response.data.data.address_line2 || '',
                        district: response.data.data.district || '',
                        state: response.data.data.state || '',
                        country: response.data.data.country || '',
                        pincode: response.data.data.pincode || '',
                    },
                    email: response.data.data.email || '',
                    phoneCode: response.data.data.phone_code || '',
                    phoneNumber: response.data.data.phone_number || '',
                    contactNumber: '',
                    bio: response.data.data.bio || '',
                    otherSpecialization: response.data.data.other_specialization || '',
                    specializations: response.data.data.specializations || [],
                    registrationNumber: response.data.data.registration_number || '',
                    medicalCouncil: response.data.data.medical_council || '',
                    yearsOfExperience: response.data.data.years_of_experience || '',
                    educationBackground: response.data.data.education_background || '',
                    previousReports: response.data.data.documents || [],
                    availability: {
                        monday: response.data.data.availability.monday || false,
                        tuesday: response.data.data.availability.tuesday || false,
                        wednesday: response.data.data.availability.wednesday || false,
                        thursday: response.data.data.availability.thursday || false,
                        friday: response.data.data.availability.friday || false,
                        saturday: response.data.data.availability.saturday || false,
                        sunday: response.data.data.availability.sunday || false,
                    },
                    available_slots: {
                        monday: response.data.data.available_slots.monday || [],
                        tuesday: response.data.data.available_slots.tuesday || [],
                        wednesday: response.data.data.available_slots.wednesday || [],
                        thursday: response.data.data.available_slots.thursday || [],
                        friday: response.data.data.available_slots.friday || [],
                        saturday: response.data.data.available_slots.saturday || [],
                        sunday: response.data.data.available_slots.sunday || [],
                    },
                    consultationFee: response.data.data.consultation_fee || '',
                    panCardNumber: response.data.data.pan_card_number || '',
                    upiId: response.data.data.upi_id || '',
                    accountNumber: response.data.data.account_number || '',
                    ifscCode: response.data.data.ifsc_code || '',
                    accountHolderName: response.data.data.account_holder_name || '',
                    bankName: response.data.data.bank_name || '',
                    nationalId: response.data.data.national_id || '',
                    website: response.data.data.website || '',
                    languagesSpoken: response.data.data.languages_spoken || '',
                    symptoms: response.data.data.symptoms || '',
                    docCategories: response.data.data.doc_categories || [],
                });
                setCategories(response.data.data.categories || []);
                setPrefixOfName(response.data.data.prefix_of_name);
                setOtherSpecialization(response.data.data.specialization !== '');
                setPreviewUrl(response.data.data.profile_picture);
                setSigPreviewUrl(response.data.data.signature_picture);
                setAdminApproved(response.data.data.admin_approved);
                dispatch(setUser({
                    name: response.data.data.name_prefix+' '+response.data.data.name,
                    email: response.data.data.email || '',
                    phone_code: response.data.data.phone_code || '',
                    phone_number: response.data.data.phone_number || '',
                    profile_pic: response.data.data.profile_picture || '',
                }));
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError('Something went wrong! Please try again later.');
        } finally {
            setBtnDisabled(false);
        }
    }, []);

    const handleOtherSpecializationChange = (e) => {
        setOtherSpecialization(e.target.checked);
    }

    const handleAvailabilityChange = (day, isChecked) => {
        setFormData(prev => ({
            ...prev,
            availability: {
                ...prev.availability,
                [day]: isChecked
            }
        }));
    };

    const addTimeSlot = (day) => {
        setFormData(prev => ({
            ...prev,
            available_slots: {
                ...prev.available_slots,
                [day]: [
                    ...prev.available_slots[day], 
                    { start: '', end: '' }
                ]
            }
        }));
    };

    const updateTimeSlot = (day, index, field, value) => {
        let updatedSlots = [...formData.available_slots[day]];
        if(updatedSlots[index][field] == value){
            return;
        }
        if(field == 'start' && updatedSlots[index].end == value){
            return;
        }
        if(field == 'end' && updatedSlots[index].start == value){
            return;
        }
        updatedSlots[index] = {
            ...updatedSlots[index],
            [field]: value,
        };
        
        setFormData(prev => ({
            ...prev,
            available_slots: {
                ...prev.available_slots,
                [day]: [
                    ...updatedSlots
                ]
            }
        }));
    };

    const removeTimeSlot = (day, index) => {
        setFormData(prev => ({
            ...prev,
            available_slots: {
                ...prev.available_slots,
                [day]: [
                    ...prev.available_slots[day].filter((_, i) => i !== index)
                ]
            }
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        setFormData(prev => ({
            ...prev,
            previousReports: [...prev.previousReports, ...files]
        }));
    };

    const handleSignaturePicUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setErrors(prev => ({
            ...prev,
            ['signature_pic']: ''
        }));

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            setErrors(prev => ({
                ...prev,
                ['signature_pic']: 'Please select a valid image file (JPEG, JPG, or PNG).'
            }));
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            setErrors(prev => ({
                ...prev,
                ['signature_pic']: 'File size exceeds 2MB. Please choose a smaller file.'
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            ['signature_pic']: file,
        }));

        // Create preview
        const reader = new FileReader();
        reader.onload = () => {
            setSigPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleResetSignaturePicUpload = () => {
        setSigPreviewUrl(getPublicImgUrl() + '/simg.jpg');
        setFormData(prev => ({
            ...prev,
            ['signature_pic']: '',
        }));
        setErrors(prev => ({
            ...prev,
            ['signature_pic']: ''
        }));
        sigFileInputRef.current.value = '';
    };

    const handleProfilePicUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setErrors(prev => ({
            ...prev,
            ['profile_pic']: ''
        }));

        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            setErrors(prev => ({
                ...prev,
                ['profile_pic']: 'Please select a valid image file (JPEG, JPG, or PNG).'
            }));
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            setErrors(prev => ({
                ...prev,
                ['profile_pic']: 'File size exceeds 2MB. Please choose a smaller file.'
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            ['profile_pic']: file,
        }));

        // Create preview
        const reader = new FileReader();
        reader.onload = () => {
            setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleResetProfilePicUpload = () => {
        setPreviewUrl(getPublicImgUrl() + '/simg.jpg');
        setFormData(prev => ({
            ...prev,
            ['profile_pic']: '',
        }));
        setErrors(prev => ({
            ...prev,
            ['profile_pic']: ''
        }));
        fileInputRef.current.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if(!validateForm()){ 
                errorDivRef.current.scrollIntoView({ behavior: 'smooth' });
                return; 
            }

            const postData = new FormData();

            // Add basic fields
            postData.append('name_prefix', formData.namePrefix);
            postData.append('name', formData.name);
            postData.append('dob', formData.dob);
            postData.append('gender', formData.gender);
            postData.append(`address_line1`, formData.address.line1);
            postData.append(`address_line2`, formData.address.line2);
            postData.append(`district`, formData.address.district);
            postData.append(`state`, formData.address.state);
            postData.append(`country`, formData.address.country);
            postData.append(`pincode`, formData.address.pincode);
            postData.append('email', formData.email);
            // postData.append('contact_number', formData.contactNumber);
            // postData.append('specialization', formData.specialization);
            postData.append('bio', formData.bio);
            postData.append('registration_number', formData.registrationNumber);
            postData.append('medical_council', formData.medicalCouncil);
            postData.append('years_of_experience', formData.yearsOfExperience);
            postData.append('education_background', formData.educationBackground);
            // formData.previousReports.forEach((file) => {
            //     postData.append('documents[]', file);
            // });
            if(formData.profile_pic){
                postData.append('profile_picture', formData.profile_pic);
            }
            if(formData.signature_pic){
                postData.append('signature_picture', formData.signature_pic);
            }
            for (let key in formData.availability) {
                postData.append(`availability[${key}]`, formData.availability[key]);
            }
            postData.append(`available_slots`, JSON.stringify(formData.available_slots));
            postData.append(`consultation_fee`, formData.consultationFee);
            postData.append(`pan_card_number`, formData.panCardNumber);
            postData.append(`upi_id`, formData.upiId);
            postData.append(`account_number`, formData.accountNumber);
            postData.append(`ifsc_code`, formData.ifscCode);
            postData.append(`account_holder_name`, formData.accountHolderName);
            postData.append(`bank_name`, formData.bankName);
            postData.append(`national_id`, formData.nationalId);
            postData.append(`website`, formData.website);
            postData.append(`languages_spoken`, formData.languagesSpoken);
            postData.append(`symptoms`, formData.symptoms);
            for (let key in formData.specializations) {
                postData.append(`specializations[${key}]`, formData.specializations[key]);
            }
            postData.append(`other_specialization`, formData.otherSpecialization);
            for (let key in formData.docCategories) {
                postData.append(`categories[${key}]`, formData.docCategories[key]);
            }

            setBtnDisabled(true);
            setIsLoading(true);
            const response = await api.post('/'+userRole+'/details', postData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response?.data?.result === 'success') {
                showSuccess(response.data.message);
                fetchDoctorData();
            }else if (response?.data?.result === 'failed') {
                showError(response?.data?.message);
                if(response?.data?.errors){
                    response?.data?.errors.forEach((item, index, arr) => {
                        showError(item);
                    });
                }
            } else {
                showError('Something went wrong! Please try again later.');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Error updating profile');
        } finally {
            setBtnDisabled(false);
            setIsLoading(false);
        }
    };

    return (
        <div className="container py-5 login-container">
            <div className="row g-3">
                <div className="col-9">
                    <h2 className="mb-4">My Profile</h2>
                </div>
                <div className="col-3 text-end">
                    <button onClick={() => window.history.go(-1)} className="btn btn-primary">Back</button>
                </div>
            </div>
            {!adminApproved && <div className="text-danger mt-2 mb-3"><small>Admin need to approve your profile data before receiving appointments.</small></div>}
            <form onSubmit={handleSubmit} className="needs-validation" ref={errorDivRef}>
                <div className="row g-3">
                    <div className="col-md-6">
                        <div className="form-group text-center">
                            <div className="img-preview-container mb-3">
                                {previewUrl && <img id="imagePreview" src={previewUrl} className="img-thumbnail" width={300} alt="Profile" />}
                            </div>
                            <input type="file" id="profilePicInput" accept="image/*" className="d-none" ref={fileInputRef} onChange={handleProfilePicUpload} />
                            <button type="button" className="btn btn-outline-primary mx-2 my-2" onClick={() => fileInputRef.current.click()}>
                                <i className="fas fa-upload mr-2"></i> Upload Profile Picture
                            </button>
                            <button type="button" className="btn btn-outline-danger mx-2 my-2" onClick={handleResetProfilePicUpload}>
                                <i className="fas fa-times mr-2"></i> Reset Profile Picture
                            </button>
                            <div className="text-muted mt-2">
                                <small>JPEG, JPG, or PNG. Max size 2MB.</small>
                            </div>
                            {errors.profile_pic && <div className="invalid-feedback d-block">{errors.profile_pic}</div>}
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group text-center">
                            <div className="img-preview-container mb-3">
                                {sigPreviewUrl && <img id="sigImagePreview" src={sigPreviewUrl} className="img-thumbnail" width={300} alt="Signature" />}
                            </div>
                            <input type="file" id="signaturePicInput" accept="image/*" className="d-none" ref={sigFileInputRef} onChange={handleSignaturePicUpload} />
                            <button type="button" className="btn btn-outline-primary mx-2 my-2" onClick={() => sigFileInputRef.current.click()}>
                                <i className="fas fa-upload mr-2"></i> Upload Signature Picture
                            </button>
                            <button type="button" className="btn btn-outline-danger mx-2 my-2" onClick={handleResetSignaturePicUpload}>
                                <i className="fas fa-times mr-2"></i> Reset Signature Picture
                            </button>
                            <div className="text-muted mt-2">
                                <small>JPEG, JPG, or PNG. Max size 2MB.</small>
                            </div>
                            {errors.signature_pic && <div className="invalid-feedback d-block">{errors.signature_pic}</div>}
                        </div>
                    </div>
                    {/* Basic Information */}
                    <div className="col-md-6">
                        <label className="form-label">Name</label>
                        <div className="input-group mb-3">
                            <select 
                                className="form-select py-3 border-primary"
                                name="namePrefix"
                                value={formData.namePrefix}
                                onChange={handleInputChange}
                                style={{ maxWidth: '120px' }}
                            >
                                <option>Choose...</option>
                                {prefixOfName.map((prefix) => (
                                    <option key={prefix} value={prefix}>{prefix}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                className="form-control py-3 border-primary"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                        </div>
                        {errors.name && <div className="invalid-feedback d-block">Please enter a valid name.</div>}
                        {errors.namePrefix && <div className="invalid-feedback d-block">{errors.namePrefix}</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Education Background</label>
                        <textarea
                            className="form-control py-3 border-primary"
                            name="educationBackground"
                            value={formData.educationBackground}
                            onChange={handleInputChange}
                            rows="1"
                            required
                        ></textarea>
                        {errors.educationBackground && <div className="invalid-feedback d-block">Please enter a valid education background.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">DOB</label>
                        <input
                            type="date"
                            className="form-control py-3 border-primary"
                            name="dob"
                            value={formData.dob}
                            onChange={handleInputChange}
                            required
                            max={new Date().toISOString().split('T')[0]}
                        />
                        {errors.dob && <div className="invalid-feedback d-block">Please enter a valid DOB (at least 18 years old).</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Gender</label>
                        <select
                            className="form-select py-3 border-primary"
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Choose...</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                        </select>
                        {errors.gender && <div className="invalid-feedback d-block">Please select a valid gender.</div>}
                    </div>

                    {/* Address Fields */}
                    <div className="col-md-6">
                        <label className="form-label">Address Line 1</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.line1"
                            value={formData.address.line1}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.address.line1 && <div className="invalid-feedback d-block">Please enter a valid address.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Address Line 2 (optional)</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.line2"
                            value={formData.address.line2}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">District</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.district"
                            value={formData.address.district}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.address.district && <div className="invalid-feedback d-block">Please enter a valid district.</div>}
                    </div>
                    <div className="col-md-4">
                        <label className="form-label">State</label>
                        <select
                            className="form-select py-3 border-primary"
                            name="address.state"
                            value={formData.address.state}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Choose...</option>
                            {indStates.map((val) => (
                                <option key={val.key} value={val.key}>
                                    {val.name}
                                </option>
                            ))}
                        </select>
                        {errors.address.state && <div className="invalid-feedback d-block">Please select a valid state.</div>}
                        {/* <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.state"
                            value={formData.address.state}
                            onChange={handleInputChange}
                            required
                        /> */}
                    </div>
                    {/* <div className="col-md-4">
                        <label className="form-label">Country</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.country"
                            value={formData.address.country}
                            onChange={handleInputChange}
                            required
                            readOnly 
                        />
                        {errors.address.country && <div className="invalid-feedback d-block">Please enter a valid country.</div>}
                    </div> */}
                    <div className="col-md-4">
                        <label className="form-label">Pincode</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="address.pincode"
                            value={formData.address.pincode}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.address.pincode && <div className="invalid-feedback d-block">Please enter a valid pincode (6-digits).</div>}
                    </div>

                    {/* Contact Information */}
                    <div className="col-md-6">
                        <label className="form-label">Email ID (optional)</label>
                        <input
                            type="email"
                            className="form-control py-3 border-primary"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                        />
                        {errors.email && <div className="invalid-feedback d-block">Please enter a valid email.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Contact Number</label>
                        <div className="input-group mb-3">
                            <select 
                                className="form-select py-3 border-primary"
                                name="phoneCode"
                                value={formData.phoneCode}
                                onChange={handleInputChange}
                                style={{ maxWidth: '120px' }}
                                disabled={true}
                            >
                                <option>Choose...</option>
                                {countryCodes.map((country) => (
                                    <option key={country.code} value={country.code}>
                                        {country.code}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="tel"
                                className="form-control py-3 border-primary"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleInputChange}
                                readOnly
                            />
                        </div>
                    </div>
                    <div className="col-12">
                        <label className="form-label">My Bio</label>
                        <textarea
                            className="form-control py-3 border-primary"
                            name="bio"
                            value={formData.bio}
                            onChange={handleInputChange}
                            rows="3"
                        ></textarea>
                    </div>

                    {/* Medical Information */}
                    {categories.length > 0 && <div className="col-12">
                        <label className="form-label">Category</label>
                        <div className="specialization-checkboxes border-primary">
                            {categories.map((category) => (
                                <div className="form-check" key={category.id}>
                                    <input
                                        type="checkbox" 
                                        name="docCategories"
                                        className="form-check-input"
                                        id={`flexCheckDefault${category.id}`}
                                        value={category.id}
                                        checked={formData.docCategories.includes(category.id)} 
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            setFormData(prev => ({
                                                ...prev,
                                                docCategories: e.target.checked 
                                                    ? [...prev.docCategories, value]
                                                    : prev.docCategories.filter(id => id !== value)
                                            }));
                                        }}
                                    />
                                    <label className="form-check-label" htmlFor={`flexCheckDefault${category.id}`}>{category.name}</label>
                                </div>
                            ))}
                        </div>
                        {errors.docCategories && <div className="invalid-feedback d-block">Please enter a valid categories.</div>}
                    </div>}
                    <div className="col-12">
                        <label className="form-label">Specialization</label>
                        <div className="specialization-checkboxes border-primary">
                            {specializations.map((specialization) => (
                                <div className="form-check" key={specialization.id}>
                                    <input
                                        type="checkbox" 
                                        name="specializations"
                                        className="form-check-input"
                                        id={`flexCheckDefault${specialization.id}`}
                                        value={specialization.id}
                                        checked={formData.specializations.includes(specialization.id)} 
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            setFormData(prev => ({
                                                ...prev,
                                                specializations: e.target.checked 
                                                    ? [...prev.specializations, value]
                                                    : prev.specializations.filter(id => id !== value)
                                            }));
                                        }}
                                    />
                                    <label className="form-check-label" htmlFor={`flexCheckDefault${specialization.id}`}>{specialization.name}</label>
                                </div>
                            ))}
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    name="specializations"
                                    className="form-check-input"
                                    id={`flexCheckDefault_other`}
                                    value={`other`}
                                    checked={otherSpecialization}
                                    onChange={handleOtherSpecializationChange}
                                />
                                <label className="form-check-label" htmlFor={`flexCheckDefault_other`}>Other</label>
                            </div>
                            {otherSpecialization && (
                            <div className="form-check">
                                <textarea
                                    className="form-control py-3 mt-3 mb-3 border-primary"
                                    name="otherSpecialization"
                                    value={formData.otherSpecialization}
                                    onChange={handleInputChange}
                                    rows="3"
                                ></textarea>
                            </div>
                            )}
                        </div>
                        {errors.specializations && <div className="invalid-feedback d-block">Please enter a valid specialization.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Registration Number</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="registrationNumber"
                            value={formData.registrationNumber}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.registrationNumber && <div className="invalid-feedback d-block">Please enter a valid registration number.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Medical Council Affiliation</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="medicalCouncil"
                            value={formData.medicalCouncil}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.medicalCouncil && <div className="invalid-feedback d-block">Please enter a valid medical council affiliation.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Years Of Experience</label>
                        <input
                            type="number"
                            className="form-control py-3 border-primary"
                            name="yearsOfExperience"
                            value={formData.yearsOfExperience}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.yearsOfExperience && <div className="invalid-feedback d-block">Please enter a valid years of experience.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">National ID / Government-issued ID</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="nationalId"
                            value={formData.nationalId}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.nationalId && <div className="invalid-feedback d-block">Please enter a valid national ID / government-issued ID.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Website (optional)</label>
                        <input
                            type="url"
                            className="form-control py-3 border-primary"
                            name="website"
                            value={formData.website}
                            onChange={handleInputChange}
                            placeholder="https://example.com"
                        />
                        {errors.website && <div className="invalid-feedback d-block">Please enter a valid website.</div>}
                    </div>
                    <div className="col-12">
                        <label className="form-label">Languages Spoken</label>
                        <textarea
                            className="form-control py-3 border-primary"
                            name="languagesSpoken"
                            value={formData.languagesSpoken}
                            onChange={handleInputChange}
                            rows="3"
                            required
                        ></textarea>
                        {errors.languagesSpoken && <div className="invalid-feedback d-block">Please enter a valid languages spoken.</div>}
                    </div>
                    <div className="col-12">
                        <label className="form-label">Symptoms Treated (optional)</label>
                        <textarea
                            className="form-control py-3 border-primary"
                            name="symptoms"
                            value={formData.symptoms}
                            onChange={handleInputChange}
                            rows="3"
                            placeholder="e.g. Fever, Headache, Chest Pain, Diabetes management..."
                        ></textarea>
                    </div>
                    <div className="col-12">
                        <label className="form-label mb-3">Available Days {/* & Time Slots*/}</label>
                        {errors.available_slots && <div className="invalid-feedback d-block">Please select a valid available slots.</div>}
                        <div className="card mb-3">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="monday-available" 
                                        checked={formData.availability.monday}
                                        onChange={(e) => handleAvailabilityChange('monday', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="monday-available">Monday</label>
                                </div>
                                {/* {formData.availability.monday && (
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => addTimeSlot('monday')}
                                    >Add Slot</button>
                                )} */}
                            </div>
                            {/* {formData.availability.monday && (
                                <div className="card-body">
                                    {formData.available_slots.monday.length > 0 ? (
                                        formData.available_slots.monday.map((slot, index) => (
                                            <div key={index} className="row mb-2 align-items-center">
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.start}
                                                        max={slot.end}
                                                        onChange={(e) => updateTimeSlot('monday', index, 'start', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 p-0 text-center">to</div>
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.end}
                                                        min={slot.start}
                                                        onChange={(e) => updateTimeSlot('monday', index, 'end', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 px-1">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeTimeSlot('monday', index)}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted">No time slots added</p>
                                    )}
                                </div>
                            )} */}
                        </div>
                        <div className="card mb-3">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="tuesday-available" 
                                        checked={formData.availability.tuesday}
                                        onChange={(e) => handleAvailabilityChange('tuesday', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="tuesday-available">Tuesday</label>
                                </div>
                                {/* {formData.availability.tuesday && (
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => addTimeSlot('tuesday')}
                                    >Add Slot</button>
                                )} */}
                            </div>
                            {/* {formData.availability.tuesday && (
                                <div className="card-body">
                                    {formData.available_slots.tuesday.length > 0 ? (
                                        formData.available_slots.tuesday.map((slot, index) => (
                                            <div key={index} className="row mb-2 align-items-center">
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.start}
                                                        max={slot.end}
                                                        onChange={(e) => updateTimeSlot('tuesday', index, 'start', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 p-0 text-center">to</div>
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.end}
                                                        min={slot.start}
                                                        onChange={(e) => updateTimeSlot('tuesday', index, 'end', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 px-1">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeTimeSlot('tuesday', index)}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted">No time slots added</p>
                                    )}
                                </div>
                            )} */}
                        </div>
                        <div className="card mb-3">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="wednesday-available" 
                                        checked={formData.availability.wednesday}
                                        onChange={(e) => handleAvailabilityChange('wednesday', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="wednesday-available">Wednesday</label>
                                </div>
                                {/* {formData.availability.wednesday && (
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => addTimeSlot('wednesday')}
                                    >Add Slot</button>
                                )} */}
                            </div>
                            {/* {formData.availability.wednesday && (
                                <div className="card-body">
                                    {formData.available_slots.wednesday.length > 0 ? (
                                        formData.available_slots.wednesday.map((slot, index) => (
                                            <div key={index} className="row mb-2 align-items-center">
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.start}
                                                        max={slot.end}
                                                        onChange={(e) => updateTimeSlot('wednesday', index, 'start', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 p-0 text-center">to</div>
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.end}
                                                        min={slot.start}
                                                        onChange={(e) => updateTimeSlot('wednesday', index, 'end', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 px-1">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeTimeSlot('wednesday', index)}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted">No time slots added</p>
                                    )}
                                </div>
                            )} */}
                        </div>
                        <div className="card mb-3">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="thursday-available" 
                                        checked={formData.availability.thursday}
                                        onChange={(e) => handleAvailabilityChange('thursday', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="thursday-available">Thursday</label>
                                </div>
                                {/* {formData.availability.thursday && (
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => addTimeSlot('thursday')}
                                    >Add Slot</button>
                                )} */}
                            </div>
                            {/* {formData.availability.thursday && (
                                <div className="card-body">
                                    {formData.available_slots.thursday.length > 0 ? (
                                        formData.available_slots.thursday.map((slot, index) => (
                                            <div key={index} className="row mb-2 align-items-center">
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.start}
                                                        max={slot.end}
                                                        onChange={(e) => updateTimeSlot('thursday', index, 'start', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 p-0 text-center">to</div>
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.end}
                                                        min={slot.start}
                                                        onChange={(e) => updateTimeSlot('thursday', index, 'end', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 px-1">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeTimeSlot('thursday', index)}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted">No time slots added</p>
                                    )}
                                </div>
                            )} */}
                        </div>
                        <div className="card mb-3">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="friday-available" 
                                        checked={formData.availability.friday}
                                        onChange={(e) => handleAvailabilityChange('friday', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="friday-available">Friday</label>
                                </div>
                                {/* {formData.availability.friday && (
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => addTimeSlot('friday')}
                                    >Add Slot</button>
                                )} */}
                            </div>
                            {/* {formData.availability.friday && (
                                <div className="card-body">
                                    {formData.available_slots.friday.length > 0 ? (
                                        formData.available_slots.friday.map((slot, index) => (
                                            <div key={index} className="row mb-2 align-items-center">
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.start}
                                                        max={slot.end}
                                                        onChange={(e) => updateTimeSlot('friday', index, 'start', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 p-0 text-center">to</div>
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.end}
                                                        min={slot.start}
                                                        onChange={(e) => updateTimeSlot('friday', index, 'end', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 px-1">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeTimeSlot('friday', index)}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted">No time slots added</p>
                                    )}
                                </div>
                            )} */}
                        </div>
                        <div className="card mb-3">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="saturday-available" 
                                        checked={formData.availability.saturday}
                                        onChange={(e) => handleAvailabilityChange('saturday', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="saturday-available">Saturday</label>
                                </div>
                                {/* {formData.availability.saturday && (
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => addTimeSlot('saturday')}
                                    >Add Slot</button>
                                )} */}
                            </div>
                            {/* {formData.availability.saturday && (
                                <div className="card-body">
                                    {formData.available_slots.saturday.length > 0 ? (
                                        formData.available_slots.saturday.map((slot, index) => (
                                            <div key={index} className="row mb-2 align-items-center">
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.start}
                                                        max={slot.end}
                                                        onChange={(e) => updateTimeSlot('saturday', index, 'start', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 p-0 text-center">to</div>
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.end}
                                                        min={slot.start}
                                                        onChange={(e) => updateTimeSlot('saturday', index, 'end', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 px-1">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeTimeSlot('saturday', index)}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted">No time slots added</p>
                                    )}
                                </div>
                            )} */}
                        </div>
                        <div className="card mb-3">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id="sunday-available" 
                                        checked={formData.availability.sunday}
                                        onChange={(e) => handleAvailabilityChange('sunday', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="sunday-available">Sunday</label>
                                </div>
                                {/* {formData.availability.sunday && (
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => addTimeSlot('sunday')}
                                    >Add Slot</button>
                                )} */}
                            </div>
                            {/* {formData.availability.sunday && (
                                <div className="card-body">
                                    {formData.available_slots.sunday.length > 0 ? (
                                        formData.available_slots.sunday.map((slot, index) => (
                                            <div key={index} className="row mb-2 align-items-center">
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.start}
                                                        max={slot.end}
                                                        onChange={(e) => updateTimeSlot('sunday', index, 'start', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 p-0 text-center">to</div>
                                                <div className="col-5 px-1">
                                                    <input 
                                                        type="time" 
                                                        className="form-control" 
                                                        value={slot.end}
                                                        min={slot.start}
                                                        onChange={(e) => updateTimeSlot('sunday', index, 'end', e.target.value)}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-1 px-1">
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => removeTimeSlot('sunday', index)}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted">No time slots added</p>
                                    )}
                                </div>
                            )} */}
                        </div>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label">Consultation Fee (₹)</label>
                        <input
                            type="number"
                            className="form-control py-3 border-primary"
                            name="consultationFee"
                            value={formData.consultationFee}
                            onChange={handleInputChange}
                            required
                            min="0"
                        />
                        {errors.consultationFee && <div className="invalid-feedback d-block">Please enter a valid consultation fee.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">PAN Card</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="panCardNumber"
                            value={formData.panCardNumber}
                            onChange={handleInputChange}
                            required
                        />
                        {errors.panCardNumber && <div className="invalid-feedback d-block">Please enter a valid PAN card number.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">UPI ID (optional)</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="upiId"
                            value={formData.upiId}
                            onChange={handleInputChange}
                        />
                        {errors.upiId && <div className="invalid-feedback d-block">Please enter a valid UPI ID.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Account Number (optional)</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="accountNumber"
                            value={formData.accountNumber}
                            onChange={handleInputChange}
                        />
                        {errors.accountNumber && <div className="invalid-feedback d-block">Please enter a valid account number.</div>}
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">IFSC Code (optional)</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="ifscCode"
                            value={formData.ifscCode}
                            onChange={handleInputChange}
                        />
                        {errors.ifscCode && <div className="invalid-feedback d-block">Please enter a valid IFSC code.</div>}
                    </div>
                    
                    <div className="col-md-6">
                        <label className="form-label">Account Holder Name (optional)</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="accountHolderName"
                            value={formData.accountHolderName}
                            onChange={handleInputChange}
                        />
                        {errors.accountHolderName && <div className="invalid-feedback d-block">Please enter a valid account holder name.</div>}
                    </div>
                    
                    <div className="col-md-6">
                        <label className="form-label">Bank Name (optional)</label>
                        <input
                            type="text"
                            className="form-control py-3 border-primary"
                            name="bankName"
                            value={formData.bankName}
                            onChange={handleInputChange}
                        />
                        {errors.bankName && <div className="invalid-feedback d-block">Please enter a valid bank name.</div>}
                    </div>

                    {/* File Upload */}
                    {/* <div className="col-12">
                        <label className="form-label">Previous Reports and Prescriptions (optional)</label>
                        <input
                            type="file"
                            className="form-control py-3 border-primary"
                            onChange={handleFileUpload}
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png"
                        />
                        {formData.previousReports.length > 0 && (
                            <div className="mt-2">
                                <p>Selected files:</p>
                                <ul className="list-group">
                                    {formData.previousReports.map((file, index) => (
                                        <li key={index} className="list-group-item">{file.name}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div> */}
                </div>

                <div className="mt-4">
                    <button type="submit" className="btn btn-primary" disabled={btnDisabled}>Save Profile</button>
                </div>
            </form>
        </div>
    );
}